import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Users, IndianRupee, Download, FileDown, ChevronDown, Youtube, Calendar } from "lucide-react";

function money(n, prefix = "$") {
  const v = Number(n || 0);
  return `${prefix}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function inr(n) { return money(n, "₹"); }

export default function Revenue() {
  const [months, setMonths] = useState([]);           // metadata (no rows)
  const [selectedId, setSelectedId] = useState("");
  const [current, setCurrent] = useState(null);       // full report
  const [downloading, setDownloading] = useState(false);
  const [manual, setManual] = useState({ total: 0, records: [] });

  useEffect(() => {
    api.get("/yt-revenue").then((r) => {
      setMonths(r.data);
      if (r.data.length > 0) setSelectedId(r.data[0].id);
    });
    api.get("/revenue").then((r) => setManual(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) { setCurrent(null); return; }
    api.get(`/yt-revenue/${selectedId}`).then((r) => setCurrent(r.data));
  }, [selectedId]);

  const previous = useMemo(() => months.filter((m) => m.id !== selectedId), [months, selectedId]);

  // Trend data — chronological (oldest → newest) so the chart reads left-to-right
  const trendData = useMemo(
    () => [...months].reverse().map((m) => ({
      month: m.month_label.replace(/(\w+)\s(\d{4})/, (_, mo, y) => `${mo.slice(0, 3)} ${y.slice(2)}`),
      revenue: Number(m.totals?.total_rev || 0),
      inr: Number(m.totals?.total_inr || 0),
      party: Number(m.totals?.total_party || 0),
    })),
    [months]
  );

  const download = async (path, filename) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("sd_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Earnings</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">Revenue</h1>

      <Tabs defaultValue="youtube" className="mt-8">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="youtube" data-testid="tab-yt-revenue"><Youtube className="w-4 h-4 mr-1.5 text-rose-400" /> YouTube Revenue</TabsTrigger>
          <TabsTrigger value="other" data-testid="tab-other-revenue"><DollarSign className="w-4 h-4 mr-1.5" /> Other Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="youtube" className="mt-6 space-y-6">
          {months.length === 0 ? (
            <div className="p-16 rounded-2xl border border-zinc-800 bg-zinc-950/60 text-center">
              <Youtube className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No YouTube revenue reports yet. Your admin will publish reports here monthly.</p>
            </div>
          ) : (
            <>
              {/* Selector + downloads */}
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-3">
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Select Month</label>
                  <Select value={selectedId} onValueChange={setSelectedId}>
                    <SelectTrigger data-testid="yt-month-select" className="bg-zinc-900 border-zinc-800 w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {months.map((m) => <SelectItem key={m.id} value={m.id}>{m.month_label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" disabled={!current || downloading}
                    onClick={() => download(`/api/yt-revenue/${selectedId}/download`, "month.xlsx")}
                    data-testid="yt-download-month" className="rounded-full border-zinc-700 hover:bg-zinc-900">
                    <Download className="w-4 h-4 mr-2" /> Download This Month Excel
                  </Button>
                  {current?.original_storage && (
                    <Button variant="outline" disabled={downloading}
                      onClick={() => download(`/api/yt-revenue/${selectedId}/download?original=true`, current?.original_filename || "original.xlsx")}
                      data-testid="yt-download-original" className="rounded-full border-zinc-700 hover:bg-zinc-900 text-cyan-400">
                      <FileDown className="w-4 h-4 mr-2" /> Original File
                    </Button>
                  )}
                  <Button onClick={() => download(`/api/yt-revenue-export/all`, "all_history.xlsx")}
                    disabled={downloading} data-testid="yt-download-all"
                    className="rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 text-zinc-950 font-semibold hover:brightness-110">
                    <FileDown className="w-4 h-4 mr-2" /> Download All Revenue
                  </Button>
                </div>
              </div>

              {/* Summary cards */}
              {current && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <SummaryCard label="Total Revenue ($)" value={money(current.totals?.total_rev)} icon={DollarSign} tone="emerald" />
                  <SummaryCard label="Total INR" value={inr(current.totals?.total_inr)} icon={IndianRupee} tone="cyan" />
                  <SummaryCard label="Total Party Amount" value={inr(current.totals?.total_party)} icon={TrendingUp} tone="violet" />
                  <SummaryCard label="Channels" value={current.totals?.channels ?? 0} icon={Users} tone="amber" />
                </div>
              )}

              {/* Trend chart */}
              {trendData.length >= 2 && (
                <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" /> Earnings Trend</h2>
                      <p className="text-xs text-zinc-500 mt-0.5">Monthly totals across your reported months</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 text-xs">
                      <Legend2 color="#10b981" label="Revenue ($)" />
                      <Legend2 color="#22d3ee" label="INR (₹)" />
                      <Legend2 color="#a78bfa" label="Party (₹)" />
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                        <XAxis dataKey="month" stroke="#71717a" tick={{ fill: "#a1a1aa", fontSize: 12 }} />
                        <YAxis yAxisId="left" stroke="#10b981" tick={{ fill: "#a1a1aa", fontSize: 11 }}
                          tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + "k" : v}`} />
                        <YAxis yAxisId="right" orientation="right" stroke="#22d3ee" tick={{ fill: "#a1a1aa", fontSize: 11 }}
                          tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                        <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8, color: "#f8fafc" }}
                          formatter={(v, k) => k === "revenue" ? [`$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`, "Revenue"]
                            : [`₹${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`, k === "inr" ? "INR" : "Party"]} />
                        <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: "#10b981" }} activeDot={{ r: 6 }} />
                        <Line yAxisId="right" type="monotone" dataKey="inr" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3, fill: "#22d3ee" }} />
                        <Line yAxisId="right" type="monotone" dataKey="party" stroke="#a78bfa" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3, fill: "#a78bfa" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Rows table */}
              {current && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
                  <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                    <h2 className="text-lg font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-400" /> {current.month_label}</h2>
                    <span className="text-xs text-zinc-500">{current.rows?.length || 0} channels</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]" data-testid="yt-rev-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                          <th className="p-3 text-right w-14">S.no</th>
                          <th className="p-3">Channel URL</th>
                          <th className="p-3">Channel Name</th>
                          <th className="p-3 text-right">Total Rev</th>
                          <th className="p-3 text-right">$ Rate</th>
                          <th className="p-3 text-right">INR</th>
                          <th className="p-3 text-right">CMS</th>
                          <th className="p-3 text-right">Party</th>
                        </tr>
                      </thead>
                      <tbody>
                        {current.rows?.map((r, i) => (
                          <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                            <td className="p-3 text-right text-zinc-500 font-mono text-xs">{r.sno || i + 1}</td>
                            <td className="p-3 text-xs">
                              {r.channel_url ? (
                                <a href={r.channel_url} target="_blank" rel="noreferrer" className="text-rose-400 hover:underline break-all">{r.channel_url}</a>
                              ) : "—"}
                            </td>
                            <td className="p-3">{r.channel_name || "—"}</td>
                            <td className="p-3 text-right font-mono text-emerald-400">{money(r.total_rev)}</td>
                            <td className="p-3 text-right font-mono text-zinc-400">{r.dollar_rate ? Number(r.dollar_rate).toFixed(2) : "—"}</td>
                            <td className="p-3 text-right font-mono text-cyan-300">{inr(r.inr)}</td>
                            <td className="p-3 text-right font-mono text-zinc-400">{r.cms || "—"}</td>
                            <td className="p-3 text-right font-mono text-violet-300">{inr(r.party)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-zinc-700 bg-zinc-900/60 font-semibold">
                          <td colSpan={3} className="p-3 text-right text-xs uppercase tracking-wider text-zinc-400">TOTAL</td>
                          <td className="p-3 text-right font-mono text-emerald-400">{money(current.totals?.total_rev)}</td>
                          <td className="p-3"></td>
                          <td className="p-3 text-right font-mono text-cyan-300">{inr(current.totals?.total_inr)}</td>
                          <td className="p-3"></td>
                          <td className="p-3 text-right font-mono text-violet-300">{inr(current.totals?.total_party)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Previous reports collapsed */}
              {previous.length > 0 && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
                  <div className="p-4 border-b border-zinc-800">
                    <h2 className="text-lg font-semibold">Previous Reports</h2>
                  </div>
                  <div className="divide-y divide-zinc-800">
                    {previous.map((m) => (
                      <Collapsible key={m.id}>
                        <CollapsibleTrigger data-testid={`prev-toggle-${m.id}`}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-zinc-900/40 transition-colors group">
                          <div>
                            <div className="font-medium">{m.month_label}</div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                              Updated {new Date(m.updated_at).toLocaleDateString()} · {m.totals?.channels || 0} channels · {money(m.totals?.total_rev)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedId(m.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                              data-testid={`prev-view-${m.id}`} className="text-cyan-400 h-8">View</Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); download(`/api/yt-revenue/${m.id}/download`, "month.xlsx"); }}
                              data-testid={`prev-download-${m.id}`} className="text-emerald-400 h-8">
                              <Download className="w-3.5 h-3.5 mr-1" /> Excel
                            </Button>
                            <ChevronDown className="w-4 h-4 text-zinc-500 group-data-[state=open]:rotate-180 transition-transform" />
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="px-4 pb-4 pt-1">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            <MiniStat label="Total Rev" value={money(m.totals?.total_rev)} />
                            <MiniStat label="Total INR" value={inr(m.totals?.total_inr)} />
                            <MiniStat label="Party" value={inr(m.totals?.total_party)} />
                            <MiniStat label="Channels" value={m.totals?.channels || 0} />
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="other" className="mt-6">
          <div className="p-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent mb-6">
            <div className="text-xs uppercase tracking-wider text-zinc-400">Manual Revenue Total</div>
            <div className="mt-2 text-4xl font-bold gradient-text">{money(manual.total)}</div>
          </div>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
            {manual.records.length === 0 ? (
              <div className="p-12 text-center text-zinc-500">No other revenue entries yet.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="p-4">Date</th>
                    <th className="p-4">Song</th>
                    <th className="p-4">Period</th>
                    <th className="p-4">Notes</th>
                    <th className="p-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {manual.records.map((r) => (
                    <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                      <td className="p-4 text-zinc-400 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="p-4">{r.song_title || "—"}</td>
                      <td className="p-4 text-zinc-400 text-sm">{r.period || "—"}</td>
                      <td className="p-4 text-zinc-400 text-sm">{r.notes || "—"}</td>
                      <td className="p-4 text-right font-mono text-emerald-400 font-semibold">{money(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone }) {
  const tones = {
    emerald: "border-emerald-500/30 from-emerald-500/15 text-emerald-400",
    cyan: "border-cyan-500/30 from-cyan-500/15 text-cyan-400",
    violet: "border-violet-500/30 from-violet-500/15 text-violet-400",
    amber: "border-amber-500/30 from-amber-500/15 text-amber-400",
  };
  const t = tones[tone] || tones.cyan;
  const [ringClass, gradClass, iconClass] = t.split(" ");
  return (
    <div className={`p-5 rounded-2xl border ${ringClass} bg-gradient-to-br ${gradClass} to-transparent`}>
      <Icon className={`w-5 h-5 ${iconClass} mb-3`} />
      <div className="text-2xl font-bold tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-zinc-400 uppercase tracking-wider">{label}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-zinc-900 border border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 font-mono text-zinc-100">{value}</div>
    </div>
  );
}
function Legend2({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 text-zinc-400">
      <span className="w-3 h-0.5 rounded" style={{ background: color }} />
      {label}
    </div>
  );
}
